/**
 * Pagina Agenda: vista unificata cross-project di tutte le attività CRM.
 * Filtri: solo pendenti, tipo, periodo (oggi/settimana/mese/tutto), cliente.
 * Click → progetto di provenienza.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Calendar as CalendarIcon, Phone, Mail, Users as UsersIcon, MapPin, StickyNote,
  Bell, ArrowRight, Filter, X,
} from 'lucide-react';
import EmptyState from '@/components/common/EmptyState';
import { useAllActivities, type EnrichedActivity } from '@/hooks/useAllActivities';
import { useProjectActivities, type ProjectActivityType } from '@/hooks/useProjectActivities';

/**
 * Riga virtuale dell'agenda. Una stessa activity può generare 2 entries quando
 * ha sia contact_at (evento storico) sia follow_up_at (richiamo programmato):
 * compaiono in due giorni distinti perché logicamente sono due eventi diversi.
 */
type AgendaEntry = {
  key: string;
  kind: 'event' | 'followup';
  iso: string;
  activity: EnrichedActivity;
};
import { useCustomers } from '@/hooks/useCustomers';

const TYPE_META: Record<ProjectActivityType, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  call:       { label: 'Chiamata',    icon: <Phone className="h-4 w-4" />,         color: 'text-blue-700',    bg: 'bg-blue-100' },
  email:      { label: 'Email',       icon: <Mail className="h-4 w-4" />,          color: 'text-indigo-700',  bg: 'bg-indigo-100' },
  meeting:    { label: 'Riunione',    icon: <UsersIcon className="h-4 w-4" />,     color: 'text-purple-700',  bg: 'bg-purple-100' },
  site_visit: { label: 'Sopralluogo', icon: <MapPin className="h-4 w-4" />,        color: 'text-emerald-700', bg: 'bg-emerald-100' },
  note:       { label: 'Nota',        icon: <StickyNote className="h-4 w-4" />,    color: 'text-slate-700',   bg: 'bg-slate-100' },
  reminder:   { label: 'Promemoria',  icon: <Bell className="h-4 w-4" />,          color: 'text-amber-700',   bg: 'bg-amber-100' },
};

type PeriodFilter = 'today' | 'week' | 'month' | 'all';

const PERIOD_LABELS: Record<PeriodFilter, string> = {
  today: 'Oggi',
  week: 'Prossimi 7 giorni',
  month: 'Prossimi 30 giorni',
  all: 'Tutte',
};

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return `Oggi ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  const diffDay = Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDay === 1) return 'Domani';
  if (diffDay === -1) return 'Ieri';
  if (diffDay > 0 && diffDay <= 7) return `Fra ${diffDay} giorni`;
  if (diffDay < 0 && diffDay >= -7) return `${Math.abs(diffDay)} giorni fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Agenda: React.FC = () => {
  const navigate = useNavigate();

  // Stato filtri
  const [onlyPending, setOnlyPending] = useState(true);
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [typeFilter, setTypeFilter] = useState<ProjectActivityType | 'all'>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');

  const upcomingDays =
    period === 'today' ? 0 :
    period === 'week' ? 7 :
    period === 'month' ? 30 :
    null;

  const { data: activities = [], isLoading } = useAllActivities({
    onlyPending,
    upcomingDays,
    types: typeFilter === 'all' ? undefined : [typeFilter],
    customerId: customerFilter === 'all' ? undefined : customerFilter,
    limit: 500,
  });

  // Per filtro cliente: solo quelli effettivamente presenti nelle attività + anagrafica
  const { data: customers = [] } = useCustomers();
  const availableCustomers = useMemo(() => {
    const ids = new Set<string>();
    for (const a of activities) {
      if (a.project?.customer_id) ids.add(a.project.customer_id);
    }
    return customers.filter(c => ids.has(c.id) || customerFilter === c.id);
  }, [activities, customers, customerFilter]);

  // Mutation: completa/riapri direttamente dall'agenda (sia contatto sia follow-up)
  const {
    completeReminder, reopenReminder,
    completeContact, reopenContact,
  } = useProjectActivities(undefined);

  const hasActiveFilters =
    !onlyPending || period !== 'all' || typeFilter !== 'all' || customerFilter !== 'all';

  const resetFilters = () => {
    setOnlyPending(true);
    setPeriod('all');
    setTypeFilter('all');
    setCustomerFilter('all');
  };

  // Splitta ogni activity in entries (event/followup), poi raggruppa per giorno.
  // Ogni entry ha un proprio stato done/pending: il contatto e il follow-up sono task indipendenti.
  const entries = useMemo<AgendaEntry[]>(() => {
    const out: AgendaEntry[] = [];
    for (const a of activities) {
      const hasContact = !!a.contact_at;
      const hasFollowUp = !!a.follow_up_at;
      const contactPending = hasContact && !a.contact_done_at;
      const followUpPending = hasFollowUp && !a.completed_at;

      if (onlyPending) {
        // Mostra ogni task pendente come entry separata
        if (contactPending) {
          out.push({ key: `${a.id}:ev`, kind: 'event', iso: a.contact_at!, activity: a });
        }
        if (followUpPending) {
          out.push({ key: `${a.id}:fu`, kind: 'followup', iso: a.follow_up_at!, activity: a });
        }
        continue;
      }

      // Vista completa: tutte le entries che esistono (sia fatte che pendenti)
      if (hasContact) {
        out.push({ key: `${a.id}:ev`, kind: 'event', iso: a.contact_at!, activity: a });
      }
      if (hasFollowUp) {
        out.push({ key: `${a.id}:fu`, kind: 'followup', iso: a.follow_up_at!, activity: a });
      }
    }
    return out;
  }, [activities, onlyPending]);

  const grouped = useMemo(() => {
    const map = new Map<string, AgendaEntry[]>();
    for (const e of entries) {
      const d = new Date(e.iso);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [entries]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8 space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-tour-id="agenda-page">Agenda</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Tutte le attività commerciali in un'unica vista cross-progetto
              </p>
            </div>

            {/* Filtri */}
            <Card>
              <CardContent className="pt-5 flex flex-wrap items-end gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtri</span>
                </div>

                {/* Solo pendenti */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="only-pending"
                    checked={onlyPending}
                    onCheckedChange={setOnlyPending}
                  />
                  <Label htmlFor="only-pending" className="text-sm cursor-pointer">
                    Solo da fare
                  </Label>
                </div>

                {/* Periodo */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Periodo</Label>
                  <Select value={period} onValueChange={(v) => setPeriod(v as PeriodFilter)}>
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PERIOD_LABELS) as PeriodFilter[]).map(p => (
                        <SelectItem key={p} value={p}>{PERIOD_LABELS[p]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tipo */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
                    <SelectTrigger className="h-9 w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i tipi</SelectItem>
                      {(Object.keys(TYPE_META) as ProjectActivityType[]).map(t => (
                        <SelectItem key={t} value={t}>
                          <span className="inline-flex items-center gap-2">
                            {TYPE_META[t].icon}
                            {TYPE_META[t].label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cliente */}
                {availableCustomers.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Cliente</Label>
                    <Select value={customerFilter} onValueChange={setCustomerFilter}>
                      <SelectTrigger className="h-9 w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti i clienti</SelectItem>
                        {availableCustomers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 ml-auto">
                    <X className="h-3 w-3" />
                    Reset
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Lista raggruppata per giorno */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  {entries.length} {entries.length === 1 ? 'voce' : 'voci'}
                </CardTitle>
                <CardDescription>
                  Ordinate cronologicamente · gli eventi con follow-up appaiono in due giorni distinti
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Caricamento…</p>
                ) : entries.length === 0 ? (
                  <EmptyState
                    icon={<CalendarIcon className="h-7 w-7" />}
                    title="Nessuna attività trovata"
                    description={
                      hasActiveFilters
                        ? 'Prova a rilassare i filtri o seleziona un periodo più ampio.'
                        : 'Quando aggiungerai chiamate, sopralluoghi o promemoria sui progetti, compariranno qui.'
                    }
                  />
                ) : (
                  <div className="space-y-5">
                    {grouped.map(([dayKey, items]) => {
                      const dayDate = new Date(dayKey + 'T00:00:00');
                      const todayStr = new Date().toISOString().slice(0, 10);
                      const isToday = dayKey === todayStr;
                      const isPast = dayDate.getTime() < new Date().setHours(0, 0, 0, 0);
                      return (
                        <div key={dayKey}>
                          <div className={`text-xs font-semibold uppercase tracking-wide mb-2 sticky top-0 bg-background py-1 z-10 ${
                            isToday ? 'text-red-700' : isPast ? 'text-muted-foreground' : 'text-foreground'
                          }`}>
                            {isToday ? 'Oggi · ' : ''}
                            {dayDate.toLocaleDateString('it-IT', {
                              weekday: 'long',
                              day: '2-digit',
                              month: 'long',
                              year: dayDate.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
                            })}
                            <span className="text-muted-foreground font-normal ml-2">
                              ({items.length})
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            {items.map(entry => {
                              const a = entry.activity;
                              const meta = TYPE_META[a.type];
                              const isFollowUp = entry.kind === 'followup';
                              const isCompleted = isFollowUp ? !!a.completed_at : !!a.contact_done_at;
                              const isOverdue = !isCompleted && new Date(entry.iso) < new Date();
                              const onToggle = (checked: boolean) => {
                                if (isFollowUp) {
                                  if (checked) completeReminder(a.id);
                                  else reopenReminder(a.id);
                                } else {
                                  if (checked) completeContact(a.id);
                                  else reopenContact(a.id);
                                }
                              };
                              return (
                                <div
                                  key={entry.key}
                                  className={`flex items-start gap-3 px-3 py-2 rounded-lg border hover:bg-muted/30 transition-colors group ${
                                    isOverdue ? 'border-red-200 bg-red-50/30' :
                                    isCompleted ? 'opacity-60' : ''
                                  }`}
                                >
                                  <Checkbox
                                    checked={isCompleted}
                                    onCheckedChange={onToggle}
                                    className="mt-1.5 shrink-0"
                                    title={isCompleted ? 'Riporta a da fare' : 'Segna come fatto'}
                                  />

                                  <div className={`p-1.5 rounded-full ${meta.bg} ${meta.color} shrink-0 mt-0.5`}>
                                    {meta.icon}
                                  </div>
                                  <button
                                    onClick={() => navigate(`/projects/${a.project_id}`)}
                                    className="flex-1 min-w-0 text-left"
                                  >
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`text-[10px] font-semibold uppercase tracking-wide ${meta.color}`}>
                                        {meta.label}
                                      </span>
                                      {isFollowUp && (
                                        <Badge variant="outline" className="text-[10px] h-4 px-1 bg-amber-50 text-amber-700 border-amber-200">
                                          Follow-up
                                        </Badge>
                                      )}
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(entry.iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                      {isOverdue && (
                                        <Badge variant="outline" className="text-[10px] h-4 px-1 bg-red-100 text-red-700 border-red-200">
                                          In ritardo
                                        </Badge>
                                      )}
                                      {isCompleted && (
                                        <Badge variant="outline" className="text-[10px] h-4 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">
                                          Fatto
                                        </Badge>
                                      )}
                                      <Badge variant="outline" className="text-[10px] h-4 px-1">
                                        {a.project?.name ?? 'Progetto'}
                                      </Badge>
                                      {(a.project?.customer_name || a.project?.client) && (
                                        <span className="text-[11px] text-muted-foreground">
                                          {a.project.customer_name ?? a.project.client}
                                        </span>
                                      )}
                                    </div>
                                    <div className={`text-sm font-medium mt-0.5 truncate ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                      {a.subject}
                                    </div>
                                    {a.body && (
                                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2 whitespace-pre-wrap">{a.body}</div>
                                    )}
                                  </button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => navigate(`/projects/${a.project_id}`)}
                                    title="Apri progetto"
                                  >
                                    <ArrowRight className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Agenda;
